import React, { useState } from "react";
import { useNeckEditor, type NeckEditorValues } from "../contexts/NeckEditorContext";

const SLIDERS: { key: keyof NeckEditorValues; label: string; min: number; max: number; step: number }[] = [
  { key: "scaleLength", label: "Longitud escala", min: 600, max: 3000, step: 50 },
  { key: "stringSpacing", label: "Espacio cuerdas", min: 20, max: 90, step: 1 },
  { key: "nutWidth", label: "Ancho cejuela", min: 4, max: 24, step: 1 },
  { key: "paddingLeft", label: "Padding izquierdo", min: 40, max: 160, step: 5 },
  { key: "paddingTop", label: "Padding superior", min: 20, max: 100, step: 5 },
  { key: "targetBoardWidth", label: "Ancho mástil", min: 800, max: 2000, step: 50 },
  { key: "noteRadius", label: "Radio notas", min: 10, max: 32, step: 1 },
  { key: "fontScale", label: "Escala fuentes", min: 0.7, max: 1.6, step: 0.05 },
];

export const NeckEditorOverlay: React.FC = () => {
  const { values, setValues, visible, setVisible, reset } = useNeckEditor();
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  if (!visible) {
    return (
      <button
        onClick={() => setVisible(true)}
        className="fixed z-[100] top-16 right-4 w-8 h-8 rounded-full flex items-center justify-center bg-white/80 backdrop-blur shadow-md border border-[#E8E0D4] text-[#8A8078] hover:text-[#2C2825] hover:bg-white transition-all"
        title="Abrir editor (Ctrl + Alt + Shift)"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
      </button>
    );
  }

  const update = (key: keyof typeof values, val: number) => {
    setValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const payload = {
        scaleLength: values.scaleLength,
        stringSpacing: values.stringSpacing,
        nutWidth: values.nutWidth,
        paddingLeft: values.paddingLeft,
        paddingTop: values.paddingTop,
      };
      const res = await fetch("/api/save-neck-config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setSaveMsg("✓ Guardado en guitarEngine.tsx");
      } else {
        setSaveMsg("✗ Error al guardar");
      }
    } catch {
      setSaveMsg("✗ Error de red");
    }
    setSaving(false);
    setTimeout(() => setSaveMsg(null), 3000);
  };

  return (
    <div
      className="fixed z-[100] top-16 right-4 w-72 rounded-xl shadow-2xl border p-4 space-y-3 overflow-y-auto"
      style={{
        backgroundColor: "#FAF6F0",
        borderColor: "#DDD5C8",
        maxHeight: "calc(100vh - 100px)",
      }}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold font-sans" style={{ color: "#2C2825" }}>
          Editor de Mástil
        </h3>
        <button
          onClick={() => setVisible(false)}
          className="w-6 h-6 flex items-center justify-center rounded text-xs"
          style={{ color: "#8A7A6A" }}
        >
          ✕
        </button>
      </div>

      <p className="text-[10px] font-sans" style={{ color: "#8A7A6A" }}>
        Ajusta en vivo y luego guarda en el código fuente.
      </p>

      {SLIDERS.map((s) => (
        <div key={s.key} className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-medium font-sans" style={{ color: "#5A524A" }}>
              {s.label}
            </label>
            <span className="text-[10px] font-mono" style={{ color: "#8A7A6A" }}>
              {values[s.key]}
            </span>
          </div>
          <input
            type="range"
            min={s.min}
            max={s.max}
            step={s.step}
            value={values[s.key]}
            onChange={(e) => update(s.key, parseFloat(e.target.value))}
            className="w-full h-1.5 rounded-lg appearance-none cursor-pointer"
            style={{
              background: "#E8E0D4",
              accentColor: "#7B2335",
            }}
          />
        </div>
      ))}

      <div className="pt-2 flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 py-2 rounded-md text-xs font-sans font-semibold transition-all"
          style={{ backgroundColor: "#7B2335", color: "#fff" }}
        >
          {saving ? "Guardando..." : "💾 Guardar en código"}
        </button>
        <button
          onClick={reset}
          className="px-3 py-2 rounded-md text-xs font-sans font-medium transition-all"
          style={{ backgroundColor: "#F0EAE0", color: "#5A524A" }}
        >
          Reset
        </button>
      </div>

      {saveMsg && (
        <p className="text-[11px] font-sans text-center font-medium" style={{ color: saveMsg.startsWith("✓") ? "#6B8E5B" : "#7B2335" }}>
          {saveMsg}
        </p>
      )}

      <p className="text-[9px] font-sans text-center" style={{ color: "#B8A99A" }}>
        Atajo: Ctrl + Alt + Shift
      </p>
    </div>
  );
};
